/**
 * GridCrash — Win Animations
 *
 * Canvas victory effects, selected by the equipped cosmetic animation:
 *   fireworks — the classic red/white/blue particle bursts
 *   rockets   — vector rocket ships streaking across the sky with trails
 *   rainbows  — ascending canvas-drawn rainbow arcs
 *
 * All strategies share the same lifecycle and the same canvas element id
 * ("fireworks-canvas") so the existing cleanup in main.ts — removing the
 * element on new game — works unchanged for every animation.
 */

import { getCachedProfile } from "../game/profile.js";
import { getEquippedLocal } from "../game/wallet.js";

export type WinAnimationId =
  | "fireworks" | "rockets" | "rainbows"
  | "confetti" | "coin_rain" | "aurora" | "supernova";

export const WIN_ANIMATIONS: Record<WinAnimationId, string> = {
  fireworks: "Fireworks",
  rockets: "Rockets",
  rainbows: "Rainbows",
  confetti: "Confetti",
  coin_rain: "Coin Rain",
  aurora: "Aurora",
  supernova: "Supernova",
};

export function getEquippedAnimation(): WinAnimationId {
  const equipped = getCachedProfile()?.equipped_animation ?? getEquippedLocal("animation");
  return equipped && equipped in WIN_ANIMATIONS
    ? (equipped as WinAnimationId)
    : "fireworks";
}

/** Plays the equipped win animation for `duration` ms. */
export function playWinAnimation(duration = 10000): void {
  playAnimationById(getEquippedAnimation(), duration);
}

/** Plays a specific animation — used by the store preview. */
export function playAnimationById(id: WinAnimationId, duration = 10000): void {
  switch (id) {
    case "rockets":   runRockets(duration);   break;
    case "rainbows":  runRainbows(duration);  break;
    case "confetti":  runConfetti(duration);  break;
    case "coin_rain": runCoinRain(duration);  break;
    case "aurora":    runAurora(duration);    break;
    case "supernova": runSupernova(duration); break;
    default:          runFireworks(duration);
  }
}

// ─── Shared canvas lifecycle ──────────────────────────────────────────────────

function createCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  document.getElementById("fireworks-canvas")?.remove();

  const canvas = document.createElement("canvas");
  canvas.id = "fireworks-canvas";
  canvas.style.cssText = `
    position: fixed; inset: 0; width: 100%; height: 100%;
    pointer-events: none; z-index: 1100;
  `;
  canvas.width  = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);

  return { canvas, ctx: canvas.getContext("2d")! };
}

// ─── Fireworks (classic) ──────────────────────────────────────────────────────

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  alpha: number;
  color: string;
  radius: number;
}

const RWB = ["#FF3131", "#FFFFFF", "#1C4DDB", "#FF6B6B", "#A8C4FF"];

function runFireworks(duration: number): void {
  const { canvas, ctx } = createCanvas();
  const particles: Particle[] = [];
  let running = true;

  function burst(x: number, y: number): void {
    const count = 60 + Math.floor(Math.random() * 40);
    for (let i = 0; i < count; i++) {
      const angle  = (Math.PI * 2 * i) / count + Math.random() * 0.3;
      const speed  = 2 + Math.random() * 5;
      particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        alpha: 1,
        color: RWB[Math.floor(Math.random() * RWB.length)],
        radius: 2 + Math.random() * 3,
      });
    }
  }

  // Continuous random bursts every 800ms for the full duration
  const burstInterval = setInterval(() => {
    if (!running) { clearInterval(burstInterval); return; }
    const px = 0.1 + Math.random() * 0.8;
    const py = 0.1 + Math.random() * 0.5;
    burst(canvas.width * px, canvas.height * py);
  }, 800);

  // Fire 3 opening bursts immediately so it starts with a bang
  burst(canvas.width * 0.25, canvas.height * 0.25);
  burst(canvas.width * 0.5,  canvas.height * 0.15);
  burst(canvas.width * 0.75, canvas.height * 0.25);

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x  += p.vx;
      p.y  += p.vy;
      p.vy += 0.12; // gravity
      p.vx *= 0.98; // drag
      p.alpha -= 0.012; // slower fade for 10s duration

      if (p.alpha <= 0) { particles.splice(i, 1); continue; }

      ctx.globalAlpha = p.alpha;
      ctx.fillStyle   = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(burstInterval);
    canvas.remove();
  }, duration);
}

// ─── Rockets ──────────────────────────────────────────────────────────────────

interface Rocket {
  x: number; y: number;
  vx: number;
  wobblePhase: number;
  scale: number;
  color: string;
  trail: Array<{ x: number; y: number; alpha: number }>;
}

const ROCKET_COLORS = ["#FF3131", "#1C4DDB", "#FFB347", "#9B59B6"];

function runRockets(duration: number): void {
  const { canvas, ctx } = createCanvas();
  const rockets: Rocket[] = [];
  let running = true;

  function launch(): void {
    const leftToRight = Math.random() > 0.35;
    const scale = 0.7 + Math.random() * 0.8;
    rockets.push({
      x: leftToRight ? -60 : canvas.width + 60,
      y: canvas.height * (0.1 + Math.random() * 0.55),
      vx: (leftToRight ? 1 : -1) * (3.5 + Math.random() * 3),
      wobblePhase: Math.random() * Math.PI * 2,
      scale,
      color: ROCKET_COLORS[Math.floor(Math.random() * ROCKET_COLORS.length)],
      trail: [],
    });
  }

  const launchInterval = setInterval(() => {
    if (!running) { clearInterval(launchInterval); return; }
    launch();
  }, 700);
  launch();
  launch();

  function drawRocket(r: Rocket): void {
    const dir = Math.sign(r.vx);
    ctx.save();
    ctx.translate(r.x, r.y);
    ctx.scale(dir * r.scale, r.scale);

    // Body
    ctx.fillStyle = r.color;
    ctx.beginPath();
    ctx.moveTo(18, 0);        // nose
    ctx.lineTo(4, -7);
    ctx.lineTo(-14, -7);
    ctx.lineTo(-14, 7);
    ctx.lineTo(4, 7);
    ctx.closePath();
    ctx.fill();

    // Fins
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(-14, -7); ctx.lineTo(-22, -13); ctx.lineTo(-14, -2);
    ctx.closePath(); ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-14, 7); ctx.lineTo(-22, 13); ctx.lineTo(-14, 2);
    ctx.closePath(); ctx.fill();

    // Porthole
    ctx.fillStyle = "#A8C4FF";
    ctx.beginPath();
    ctx.arc(0, 0, 3.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = rockets.length - 1; i >= 0; i--) {
      const r = rockets[i];
      r.x += r.vx;
      r.wobblePhase += 0.12;
      r.y += Math.sin(r.wobblePhase) * 0.8;

      r.trail.unshift({ x: r.x - Math.sign(r.vx) * 16 * r.scale, y: r.y, alpha: 0.9 });
      if (r.trail.length > 24) r.trail.pop();

      for (const t of r.trail) {
        t.alpha -= 0.035;
        if (t.alpha <= 0) continue;
        ctx.globalAlpha = t.alpha;
        ctx.fillStyle = "#FFB347";
        ctx.beginPath();
        ctx.arc(t.x, t.y, 3 * r.scale * t.alpha + 1, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      drawRocket(r);

      if ((r.vx > 0 && r.x > canvas.width + 80) || (r.vx < 0 && r.x < -80)) {
        rockets.splice(i, 1);
      }
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(launchInterval);
    canvas.remove();
  }, duration);
}

// ─── Confetti ─────────────────────────────────────────────────────────────────

interface ConfettiPiece {
  x: number; y: number;
  vx: number; vy: number;
  w: number; h: number;
  angle: number; spin: number;
  color: string;
  sway: number;
}

const CONFETTI_COLORS = ["#FF3131", "#FFB347", "#FFE156", "#4CD964", "#1C4DDB", "#9B59B6", "#FF6FD8"];

function runConfetti(duration: number): void {
  const { canvas, ctx } = createCanvas();
  const pieces: ConfettiPiece[] = [];
  let running = true;

  function spawn(count: number): void {
    for (let i = 0; i < count; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 40,
        vx: (Math.random() - 0.5) * 1.5,
        vy: 1.5 + Math.random() * 2.5,
        w: 6 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.25,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        sway: Math.random() * Math.PI * 2,
      });
    }
  }

  const spawnInterval = setInterval(() => {
    if (!running) { clearInterval(spawnInterval); return; }
    spawn(18);
  }, 250);
  spawn(120);

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = pieces.length - 1; i >= 0; i--) {
      const p = pieces[i];
      p.sway += 0.06;
      p.x += p.vx + Math.sin(p.sway) * 0.8;
      p.y += p.vy;
      p.angle += p.spin;

      if (p.y > canvas.height + 30) { pieces.splice(i, 1); continue; }

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      // Fold effect: scale width with a cosine so pieces appear to tumble
      ctx.scale(Math.cos(p.sway * 1.3), 1);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(spawnInterval);
    canvas.remove();
  }, duration);
}

// ─── Coin Rain ────────────────────────────────────────────────────────────────

interface Coin {
  x: number; y: number;
  vy: number;
  size: number;
  spinPhase: number;
  bounced: boolean;
}

function runCoinRain(duration: number): void {
  const { canvas, ctx } = createCanvas();
  const coins: Coin[] = [];
  let running = true;
  const floor = canvas.height - 20;

  function spawn(count: number): void {
    for (let i = 0; i < count; i++) {
      coins.push({
        x: Math.random() * canvas.width,
        y: -30 - Math.random() * 60,
        vy: 3 + Math.random() * 3,
        size: 22 + Math.random() * 18,
        spinPhase: Math.random() * Math.PI * 2,
        bounced: false,
      });
    }
  }

  const spawnInterval = setInterval(() => {
    if (!running) { clearInterval(spawnInterval); return; }
    spawn(6);
  }, 300);
  spawn(24);

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = coins.length - 1; i >= 0; i--) {
      const c = coins[i];
      c.y += c.vy;
      c.vy += 0.15;
      c.spinPhase += 0.15;

      if (!c.bounced && c.y >= floor) {
        c.bounced = true;
        c.vy = -c.vy * 0.45;
      }
      if (c.bounced && c.y > canvas.height + 40) { coins.splice(i, 1); continue; }

      ctx.save();
      ctx.translate(c.x, c.y);
      // Horizontal squash fakes a 3D spin on the emoji glyph
      ctx.scale(Math.abs(Math.cos(c.spinPhase)) * 0.7 + 0.3, 1);
      ctx.font = `${c.size}px serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("🪙", 0, 0);
      ctx.restore();
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(spawnInterval);
    canvas.remove();
  }, duration);
}

// ─── Aurora ───────────────────────────────────────────────────────────────────

interface Ribbon {
  baseY: number;
  amplitude: number;
  wavelength: number;
  phase: number;
  speed: number;
  thickness: number;
  hue: number;
  alpha: number;
}

function runAurora(duration: number): void {
  const { canvas, ctx } = createCanvas();
  let running = true;

  const ribbons: Ribbon[] = [];
  for (let i = 0; i < 5; i++) {
    ribbons.push({
      baseY: canvas.height * (0.12 + i * 0.09),
      amplitude: 30 + Math.random() * 50,
      wavelength: 250 + Math.random() * 250,
      phase: Math.random() * Math.PI * 2,
      speed: 0.008 + Math.random() * 0.012,
      thickness: 40 + Math.random() * 50,
      hue: 100 + i * 45, // greens → blues → violets
      alpha: 0.16 + Math.random() * 0.1,
    });
  }

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = "lighter";

    for (const r of ribbons) {
      r.phase += r.speed;
      const grad = ctx.createLinearGradient(0, r.baseY - r.thickness, 0, r.baseY + r.thickness);
      grad.addColorStop(0, `hsla(${r.hue}, 90%, 60%, 0)`);
      grad.addColorStop(0.5, `hsla(${r.hue}, 90%, 60%, ${r.alpha})`);
      grad.addColorStop(1, `hsla(${r.hue}, 90%, 60%, 0)`);
      ctx.fillStyle = grad;

      ctx.beginPath();
      ctx.moveTo(0, r.baseY - r.thickness);
      for (let x = 0; x <= canvas.width; x += 24) {
        const wave = Math.sin(x / r.wavelength + r.phase) * r.amplitude;
        ctx.lineTo(x, r.baseY + wave - r.thickness);
      }
      for (let x = canvas.width; x >= 0; x -= 24) {
        const wave = Math.sin(x / r.wavelength + r.phase) * r.amplitude;
        ctx.lineTo(x, r.baseY + wave + r.thickness);
      }
      ctx.closePath();
      ctx.fill();
    }

    ctx.globalCompositeOperation = "source-over";
    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    canvas.remove();
  }, duration);
}

// ─── Supernova ────────────────────────────────────────────────────────────────

interface Star {
  x: number; y: number;
  radius: number;
  twinkle: number;
}

interface Shockwave {
  radius: number;
  speed: number;
  alpha: number;
}

function runSupernova(duration: number): void {
  const { canvas, ctx } = createCanvas();
  let running = true;

  const cx = canvas.width / 2;
  const cy = canvas.height * 0.4;
  const stars: Star[] = [];
  for (let i = 0; i < 120; i++) {
    stars.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius: 0.5 + Math.random() * 1.8,
      twinkle: Math.random() * Math.PI * 2,
    });
  }

  const waves: Shockwave[] = [];
  function detonate(): void {
    waves.push({ radius: 0, speed: 9, alpha: 0.9 });
    waves.push({ radius: 0, speed: 6, alpha: 0.6 });
  }
  detonate();
  const waveInterval = setInterval(() => {
    if (!running) { clearInterval(waveInterval); return; }
    detonate();
  }, 2500);

  let coreFlash = 1;

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Starfield
    for (const s of stars) {
      s.twinkle += 0.05;
      ctx.globalAlpha = 0.4 + Math.sin(s.twinkle) * 0.3;
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Core glow
    coreFlash = Math.max(0.35, coreFlash - 0.006);
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 140);
    glow.addColorStop(0, `rgba(255, 245, 200, ${coreFlash})`);
    glow.addColorStop(0.4, `rgba(255, 179, 71, ${coreFlash * 0.6})`);
    glow.addColorStop(1, "rgba(255, 49, 49, 0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 140, 0, Math.PI * 2);
    ctx.fill();

    // Expanding shockwave rings
    for (let i = waves.length - 1; i >= 0; i--) {
      const w = waves[i];
      w.radius += w.speed;
      w.alpha -= 0.006;
      if (w.alpha <= 0) { waves.splice(i, 1); continue; }

      ctx.globalAlpha = w.alpha;
      ctx.lineWidth = 6;
      ctx.strokeStyle = "#FFE156";
      ctx.beginPath();
      ctx.arc(cx, cy, w.radius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.lineWidth = 2;
      ctx.strokeStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(cx, cy, w.radius * 0.92, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(waveInterval);
    canvas.remove();
  }, duration);
}

// ─── Rainbows ─────────────────────────────────────────────────────────────────

interface Rainbow {
  cx: number;
  y: number;          // vertical center — ascends over time
  radius: number;
  vy: number;
  alpha: number;
  grow: number;       // 0→1 draw-in progress
}

const RAINBOW_BANDS = ["#FF3131", "#FFB347", "#FFE156", "#4CD964", "#1C4DDB", "#9B59B6"];

function runRainbows(duration: number): void {
  const { canvas, ctx } = createCanvas();
  const rainbows: Rainbow[] = [];
  let running = true;

  function spawn(): void {
    rainbows.push({
      cx: canvas.width * (0.15 + Math.random() * 0.7),
      y: canvas.height + 40,
      radius: 50 + Math.random() * 90,
      vy: -(0.8 + Math.random() * 0.9),
      alpha: 0.9,
      grow: 0,
    });
  }

  const spawnInterval = setInterval(() => {
    if (!running) { clearInterval(spawnInterval); return; }
    spawn();
  }, 900);
  spawn();
  spawn();

  function frame(): void {
    if (!running) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = rainbows.length - 1; i >= 0; i--) {
      const rb = rainbows[i];
      rb.y += rb.vy;
      rb.grow = Math.min(1, rb.grow + 0.03);
      if (rb.y < -rb.radius) { rainbows.splice(i, 1); continue; }
      // Fade out over the top third of the screen
      if (rb.y < canvas.height * 0.33) rb.alpha -= 0.012;
      if (rb.alpha <= 0) { rainbows.splice(i, 1); continue; }

      const bandWidth = Math.max(3, rb.radius * 0.09);
      const sweep = Math.PI * rb.grow; // arcs draw in from the left

      ctx.globalAlpha = rb.alpha;
      ctx.lineWidth = bandWidth;
      ctx.lineCap = "round";
      for (let b = 0; b < RAINBOW_BANDS.length; b++) {
        ctx.strokeStyle = RAINBOW_BANDS[b];
        ctx.beginPath();
        ctx.arc(rb.cx, rb.y, rb.radius - b * bandWidth, Math.PI, Math.PI + sweep);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  setTimeout(() => {
    running = false;
    clearInterval(spawnInterval);
    canvas.remove();
  }, duration);
}
